import type { ConsoleUsage, ConsoleUsageGroup } from '@plato/shared';
import { useCallback, useState } from 'react';
import { DataTable } from '../../components/data-table.tsx';
import { Field } from '../../components/field.tsx';
import { formatCount } from '../../components/format.ts';
import { Loaded, Page } from '../../components/page.tsx';
import { useTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import type { DayRange } from '../../data/usage-data.ts';
import { ClientLimits } from './client-limits.tsx';
import { DailyCreditsChart } from './daily-credits-chart.tsx';
import { DateRangePicker, presetRange } from './date-range.tsx';
import { formatCompact, formatCredits, formatDuration, purposeLabel } from './format.ts';
import './usage.css';

export function UsageSection() {
  const [range, setRange] = useState<DayRange>(() => presetRange('this-month'));
  const load = useCallback(
    (data: ConsoleData, tenant: TenantSummary) => data.getUsage(tenant.id, range),
    [range],
  );
  const usage = useTenantQuery(load);
  return (
    <Page
      title="Usage & costs"
      summary="Credits your users and your team spent, by day, client, plan and purpose."
    >
      <DateRangePicker value={range} onChange={setRange} />
      <Loaded query={usage}>
        {(value) => (
          <div className={value.from === range.from && value.to === range.to ? '' : 'refetching'}>
            <UsageReport usage={value} />
          </div>
        )}
      </Loaded>
      <ClientLimits />
    </Page>
  );
}

function UsageReport({ usage }: { usage: ConsoleUsage }) {
  const { totals } = usage;
  return (
    <>
      <div className="hero">
        <span className="hero-label">Credits used</span>
        <span className="hero-value">{formatCredits(totals.credit_micros)}</span>
      </div>
      <dl className="stats">
        <Field label="Replies" value={formatCount(totals.runs)} />
        <Field label="Input tokens" value={formatCompact(totals.input_tokens)} />
        <Field label="Output tokens" value={formatCompact(totals.output_tokens)} />
        <Field label="Time running code" value={formatDuration(totals.sandbox_ms)} />
      </dl>
      {totals.unpriced_runs > 0 && (
        <p className="notice notice-warning" role="status">
          {formatCount(totals.unpriced_runs)} replies in this range have no price yet and count as
          zero credits here.
        </p>
      )}
      <section aria-labelledby="credits-by-day">
        <h2 id="credits-by-day">Credits per day</h2>
        <DailyCreditsChart days={usage.by_day} />
      </section>
      <div className="usage-groups">
        <GroupTable
          caption="By client"
          groups={usage.by_organization}
          label={(key) => key ?? 'No organization'}
        />
        <GroupTable caption="By plan" groups={usage.by_plan} label={(key) => key ?? 'No plan'} />
        <GroupTable caption="By purpose" groups={usage.by_purpose} label={purposeLabel} />
      </div>
    </>
  );
}

function GroupTable(props: {
  caption: string;
  groups: readonly ConsoleUsageGroup[];
  label: (key: string | null) => string;
}) {
  if (props.groups.length === 0) return null;
  return (
    <DataTable
      caption={props.caption}
      columns={[
        { header: 'Name', cell: (row) => props.label(row.key) },
        {
          header: 'Credits',
          cell: (row) => <span className="numeric">{formatCredits(row.credit_micros)}</span>,
        },
        {
          header: 'Replies',
          cell: (row) => <span className="numeric">{formatCount(row.runs)}</span>,
        },
      ]}
      rows={props.groups.map((group) => ({ ...group, id: group.key ?? '(none)' }))}
    />
  );
}
